import { useEffect, useState } from "react";
import {
  Building2, ShieldCheck, FileText, LifeBuoy, Mail, Phone, MapPin,
  ChevronRight, CheckCircle2, Clock, MessageSquare, Truck, Wrench,
} from "lucide-react";
import { Link } from "react-router-dom";
import api from "@/lib/api";

function useCompany() {
  const [c, setC] = useState(null);
  useEffect(() => { api.get("/settings/company").then((r) => setC(r.data)).catch(() => setC({})); }, []);
  return c || {};
}

function PageHero({ icon: Icon, eyebrow, title, subtitle, image }) {
  return (
    <div className="relative overflow-hidden rounded-3xl bg-[#0F172A] mb-8">
      {image && <img src={image} alt="" className="absolute inset-0 w-full h-full object-cover" />}
      <div className="absolute inset-0 bg-gradient-to-r from-[#0F172A] via-[#0F172A]/85 to-[#0F172A]/40" />
      <div className="relative px-6 sm:px-10 py-10 sm:py-14 max-w-2xl">
        <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md border border-white/20 text-white text-xs font-mono uppercase tracking-widest px-3 py-1 rounded-full">
          <Icon className="w-3.5 h-3.5 text-[#E08B00]" /> {eyebrow}
        </div>
        <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white mt-4">{title}</h1>
        {subtitle && <p className="text-slate-200 mt-3 text-sm sm:text-base leading-relaxed max-w-xl">{subtitle}</p>}
      </div>
    </div>
  );
}

const HERO_ABOUT = "https://images.unsplash.com/photo-1551038247-3d9af20df552?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
const HERO_TERMS = "https://images.unsplash.com/photo-1479839672679-a46483c0e7c8?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

export function About() {
  const c = useCompany();
  const stats = [
    { k: "Matériaux certifiés", v: "100%" },
    { k: "Zones couvertes", v: "Lomé · Agoè · Baguida" },
    { k: "Livraison & pose", v: "Express" },
  ];
  return (
    <div className="max-w-3xl mx-auto pb-4">
      <PageHero icon={Building2} eyebrow="Qui sommes-nous" title="À propos de TOGOBATI"
        subtitle="La plateforme togolaise de vente de matériaux de construction et de services d'installation — du matériau jusqu'à la pose, partout à Lomé, Agoè et Baguida."
        image={HERO_ABOUT} />

      <div className="grid grid-cols-3 gap-3 mb-8">
        {stats.map((s) => (
          <div key={s.k} className="bg-white border border-slate-200 rounded-2xl p-4 text-center">
            <p className="font-display text-lg sm:text-2xl font-extrabold text-[#C85A32] leading-tight">{s.v}</p>
            <p className="text-[11px] text-slate-500 mt-1">{s.k}</p>
          </div>
        ))}
      </div>

      <div className="grid sm:grid-cols-2 gap-4 mb-8">
        <div className="bg-white border border-slate-200 rounded-2xl p-6">
          <h2 className="font-display text-xl font-bold text-[#0F172A] mb-2">Notre mission</h2>
          <p className="text-sm text-slate-600 leading-relaxed">Simplifier vos projets de construction : commandez vos matériaux, demandez un devis et suivez vos livraisons et installations, le tout au même endroit.</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-2xl p-6">
          <h2 className="font-display text-xl font-bold text-[#0F172A] mb-2">Nos services</h2>
          <p className="text-sm text-slate-600 leading-relaxed">Vente de matériaux, livraison à domicile, installation par des professionnels certifiés, devis personnalisés et suivi en temps réel.</p>
        </div>
      </div>

      <div className="bg-white border border-slate-200 border-l-4 border-l-[#C85A32] rounded-2xl p-6">
        <h2 className="font-display text-xl font-bold text-[#0F172A] mb-4">{c.name || "TOGOBATI"}</h2>
        <div className="grid sm:grid-cols-2 gap-3">
          {c.address && <p className="flex items-center gap-2 text-sm text-slate-600"><MapPin className="w-4 h-4 text-[#C85A32] shrink-0" /> {c.address}</p>}
          {c.phone && <p className="flex items-center gap-2 text-sm text-slate-600"><Phone className="w-4 h-4 text-[#C85A32] shrink-0" /> {c.phone}</p>}
          {c.email && <p className="flex items-center gap-2 text-sm text-slate-600"><Mail className="w-4 h-4 text-[#C85A32] shrink-0" /> {c.email}</p>}
        </div>
        {(c.rccm || c.nif) && (
          <div className="flex flex-wrap gap-4 mt-4 pt-4 border-t border-slate-100">
            {c.rccm && <span className="text-xs text-slate-400">RCCM : <span className="font-num text-slate-600">{c.rccm}</span></span>}
            {c.nif && <span className="text-xs text-slate-400">NIF : <span className="font-num text-slate-600">{c.nif}</span></span>}
          </div>
        )}
      </div>
    </div>
  );
}

function LegalPage({ icon, eyebrow, title, subtitle, sections }) {
  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  return (
    <div className="max-w-4xl mx-auto pb-4">
      <PageHero icon={icon} eyebrow={eyebrow} title={title} subtitle={subtitle} image={HERO_TERMS} />
      <div className="grid lg:grid-cols-[220px_1fr] gap-6">
        <aside className="hidden lg:block">
          <div className="sticky top-24 bg-white border border-slate-200 rounded-2xl p-4">
            <p className="font-mono text-[11px] uppercase tracking-widest text-[#C85A32] mb-3">Sommaire</p>
            <nav className="space-y-1">
              {sections.map((s, i) => (
                <button key={i} onClick={() => scrollTo(`sec-${i}`)} className="w-full text-left text-sm text-slate-600 hover:text-[#C85A32] py-1.5 flex items-center gap-1.5 transition" data-testid={`toc-${i}`}>
                  <ChevronRight className="w-3.5 h-3.5 shrink-0" /> <span className="line-clamp-1">{s.title}</span>
                </button>
              ))}
            </nav>
          </div>
        </aside>
        <div className="space-y-4">
          {sections.map((s, i) => (
            <div key={i} id={`sec-${i}`} className="bg-white border border-slate-200 rounded-2xl p-6 scroll-mt-24">
              <div className="flex items-start gap-3">
                <span className="font-display font-black text-[#C85A32] text-2xl leading-none shrink-0">{String(i + 1).padStart(2, "0")}</span>
                <div>
                  <h2 className="font-display text-lg font-bold text-[#0F172A]">{s.title}</h2>
                  <p className="text-sm text-slate-600 leading-relaxed mt-1.5">{s.body}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function Terms() {
  return (
    <LegalPage icon={FileText} eyebrow="Informations légales" title="Termes & conditions"
      subtitle="Les conditions générales régissant l'utilisation de TOGOBATI et l'achat de matériaux et services d'installation."
      sections={[
        { title: "Objet", body: "Les présentes conditions générales régissent l'utilisation de la plateforme TOGOBATI et l'achat de matériaux ainsi que la réservation de services d'installation." },
        { title: "Commandes", body: "Toute commande passée sur la plateforme est ferme après confirmation. Les prix sont indiqués en FCFA, toutes taxes comprises le cas échéant." },
        { title: "Livraison", body: "Les délais et frais de livraison dépendent de la zone sélectionnée. Le paiement s'effectue à la livraison ou au bureau (Mobile Money / espèces)." },
        { title: "Installation", body: "Les prestations d'installation sont réalisées par des professionnels partenaires. Le client doit garantir l'accès au site à la date convenue." },
        { title: "Retours & réclamations", body: "Toute réclamation doit être signalée via le module Support dans un délai raisonnable après réception." },
        { title: "Responsabilité", body: "TOGOBATI met tout en œuvre pour assurer la qualité des produits et services, sans garantie au-delà des dispositions légales applicables au Togo." },
      ]} />
  );
}

export function Privacy() {
  return (
    <LegalPage icon={ShieldCheck} eyebrow="Vos données" title="Politique de confidentialité"
      subtitle="Comment nous collectons, utilisons et protégeons vos informations personnelles."
      sections={[
        { title: "Données collectées", body: "Nous collectons les informations nécessaires au traitement de vos commandes : nom, coordonnées, adresses de livraison et historique d'achats." },
        { title: "Utilisation", body: "Vos données servent uniquement à traiter vos commandes, organiser les livraisons/installations et vous informer de leur avancement." },
        { title: "Partage", body: "Vos coordonnées de livraison peuvent être transmises au livreur et au professionnel affectés, strictement pour la réalisation de la prestation." },
        { title: "Sécurité", body: "Nous ne demandons jamais votre mot de passe par email. Vos données sont stockées de manière sécurisée." },
        { title: "Vos droits", body: "Vous pouvez demander l'accès, la rectification ou la suppression de vos données via le module Support." },
      ]} />
  );
}

export function Support() {
  const c = useCompany();
  const faqs = [
    { q: "Comment suivre ma commande ?", a: "Rendez-vous dans « Suivi » depuis le menu : chaque étape (préparation, livraison, installation) y est affichée en temps réel." },
    { q: "Quels moyens de paiement acceptez-vous ?", a: "Le paiement s'effectue à la livraison (Mobile Money ou espèces). Le paiement en ligne arrive bientôt." },
    { q: "Proposez-vous l'installation ?", a: "Oui, sur de nombreux produits. Un prix fixe ou un devis vous est proposé directement sur la fiche produit." },
  ];
  return (
    <div className="max-w-3xl mx-auto pb-4">
      <PageHero icon={LifeBuoy} eyebrow="Assistance" title="Support & aide"
        subtitle="Une question sur une commande, une livraison ou une installation ? Notre équipe vous répond." image={HERO_ABOUT} />

      <div className="flex items-center gap-2 mb-5">
        <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
        <span className="text-sm text-slate-600"><span className="font-semibold">Ouvert</span> · Lun–Sam, 8h–18h (GMT)</span>
      </div>

      <div className="grid sm:grid-cols-3 gap-3 mb-6">
        {c.phone && (
          <a href={`tel:${c.phone}`} className="group flex flex-col gap-2 p-5 rounded-2xl border border-slate-200 bg-white hover:ring-1 hover:ring-[#C85A32]/30 hover:-translate-y-0.5 transition" data-testid="support-call">
            <Phone className="w-5 h-5 text-[#C85A32]" />
            <p className="font-semibold text-slate-800 group-hover:text-[#C85A32]">Appelez-nous</p>
            <p className="text-xs text-slate-500 font-num">{c.phone}</p>
          </a>
        )}
        {c.email && (
          <a href={`mailto:${c.email}`} className="group flex flex-col gap-2 p-5 rounded-2xl border border-slate-200 bg-white hover:ring-1 hover:ring-[#C85A32]/30 hover:-translate-y-0.5 transition" data-testid="support-email">
            <Mail className="w-5 h-5 text-[#C85A32]" />
            <p className="font-semibold text-slate-800 group-hover:text-[#C85A32]">Écrivez-nous</p>
            <p className="text-xs text-slate-500">{c.email}</p>
          </a>
        )}
        <Link to="/messages" className="group flex flex-col gap-2 p-5 rounded-2xl border border-slate-200 bg-white hover:ring-1 hover:ring-[#C85A32]/30 hover:-translate-y-0.5 transition" data-testid="support-message">
          <MessageSquare className="w-5 h-5 text-[#C85A32]" />
          <p className="font-semibold text-slate-800 group-hover:text-[#C85A32]">Messagerie</p>
          <p className="text-xs text-slate-500">Discuter avec l'administration</p>
        </Link>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl p-6">
        <h2 className="font-display text-xl font-bold text-[#0F172A] mb-4">Questions fréquentes</h2>
        <div className="divide-y divide-slate-100">
          {faqs.map((f, i) => (
            <details key={i} className="py-3 group" data-testid={`faq-${i}`}>
              <summary className="flex items-center gap-2 cursor-pointer list-none font-semibold text-slate-800 text-sm">
                <CheckCircle2 className="w-4 h-4 text-[#C85A32] shrink-0" /> {f.q}
              </summary>
              <p className="text-sm text-slate-600 mt-2 pl-6 leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>
      </div>
    </div>
  );
}
